import { CityChallengeStats, GameProgress, Location, LoginOrigin } from '../types';
import { GoogleTagEvent, logWithGoogleTag } from '../helpers/api/api';
import { LeadSubmission, getCurrentManiaLocation, submitLead } from '../helpers/api/mania-api';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { delay, isDefined, setManifest, setMetaTag } from '../helpers/utils';
import { login, updateAccountGameProgress, upgradeToken } from '../helpers/api/account-api';

import { GOOGLE_KEY } from '../helpers/api/google-api';
import { ProxiInputPlace } from '../components/common/proxi-input-place';
import { TextField } from '@material/mwc-textfield';
import accountInfo from '../helpers/state/account-info';
import { detectMobileViewport } from '../helpers/device';
import { getMainPage } from '../helpers/page-name';
import { validateEmail } from '../helpers/strings';

interface Guess {
    location: string;
    distance: number;
    direction: string;
    lat: number;
    lng: number;
}

interface GameState {
    guesses: Guess[];
    isWon: boolean;
    isGameOver: boolean;
    targetCity: string;
    targetLat: number;
    targetLng: number;
    mainStreetName?: string;
    mainStreetWebsite?: string;
    mainStreetTitle?: string;
}

interface Community {
    title: string;
    cityState: string;
    website: string | null;
    tag: string;
    lat: number;
    lng: number;
    markerTitle: string;
}

interface LocalPlayerData {
    totalGamesPlayed: number;
    totalWins: number;
    currentStreak: number;
    longestStreak: number;
    lastPlayedDate: string;
    dailyScores: { [date: string]: { won: boolean; guesses: number; city: string } };
}

@customElement('city-radius-challenge-page')
export class CityRadiusChallengeePage extends LitElement {

    @query('#map')
    googleMapComponent!: HTMLElement | any;

    @state()
    private gameState: GameState = {
        guesses: [],
        isWon: false,
        isGameOver: false,
        targetCity: '',
        targetLat: 0,
        targetLng: 0
    };

    @state()
    private readonly markers: google.maps.Marker[] = [];

    @state()
    private readonly circles: google.maps.Circle[] = [];

    @state()
    private mapReady: boolean = false;

    @state()
    private isLoggedIn: boolean = false;

    @state()
    private userEmail: string = '';

    @state()
    private showWinModal: boolean = false;

    @state()
    private showLeaderboard: boolean = false;

    @state()
    private showRevealButton = false;

    @state()
    private showMainStreetInfo = false;

    @state()
    private leaderboardData: Array<{ email: string; score: number; streak: number }> = [];

    @state()
    private playerData: LocalPlayerData = {
        totalGamesPlayed: 0,
        totalWins: 0,
        currentStreak: 0,
        longestStreak: 0,
        lastPlayedDate: '',
        dailyScores: {}
    };

    @state()
    private showIntakeForm: boolean = false;

    @state()
    private welcomeMessage = '';

    // Blog links for the "Latest on the Blog" section
    @state()
    private readonly blogLinks = [
        {
            title: 'Building a Digital Main Street: How to Build an Engaging Main Street Map',
            url: 'https://proxi.co/blog/digital-main-street-maps',
            description: 'A practical guide to designing a map that engages visitors and supports your district.'
        },
        {
            title: 'Why Every Main Street Needs an Interactive, Digital Map',
            url: 'https://proxi.co/blog/why-every-main-street-needs-an-interactive-digital-map',
            description: 'How interactive maps boost visibility, foot traffic, and economic activity.'
        },
        {
            title: 'Crowdsourcing Community Input for Main Street Improvements',
            url: 'https://proxi.co/blog/crowdsourcing-community-input-for-main-street-improvements',
            description: 'Turn resident ideas into actionable improvements using modern mapping tools.'
        },
        {
            title: 'Mapping Your Main Street | Proxi Interactive Maps | Custom Maps',
            url: 'https://proxi.co/maps-for/main-streets',
            description: 'See how Main Streets use Proxi for events, tourism, and business engagement.'
        },
        {
            title: 'Best of Voting for Main Streets: Engage, Celebrate, and Elevate Your Downtown District',
            url: 'https://proxi.co/blog/best-of-voting-for-main-streets-engage-celebrate-and-elevate-your-downtown-district',
            description: 'Run community voting events that build pride and highlight local favorites.'
        },
        {
            title: 'Digital Scavenger Hunts for Main Streets & Downtown Organizations',
            url: 'https://proxi.co/blog/digital-scavenger-hunts-for-main-streets-downtown-organizations',
            description: 'Create fun, interactive scavenger hunts that drive exploration and foot traffic.'
        },
        {
            title: 'Put Your Data to Work: Smarter Placemaking for Main Streets',
            url: 'https://proxi.co/blog/put-your-data-to-work-smarter-placemaking-for-main-streets',
            description: 'Use data insights to strengthen placemaking and support local businesses.'
        },
        {
            title: 'How to Measure Your Main Street Marketing Impact with Proxi Maps',
            url: 'https://proxi.co/blog/how-to-measure-your-main-street-marketing-impact-with-proxi-maps',
            description: 'Track engagement, reach, and foot traffic to understand what’s working.'
        },
        {
            title: 'New! Let Map Viewers Contact Businesses Directly with Point Inquiry',
            url: 'https://proxi.co/blog/new-let-map-viewers-contact-businesses-directly-with-point-inquiry',
            description: 'A new feature that helps businesses convert map views into real inquiries.'
        }
    ];

    private showFeatureForm() {
        this.showIntakeForm = true;
    }

    private closeIntakeForm() {
        this.showIntakeForm = false;
    }

    private async submitIntakeForm(event: Event) {
        event.preventDefault();
        
        // Find the form element - it might be the target or we need to traverse up
        let formElement: HTMLFormElement | null = null;
        
        if (event.target instanceof HTMLFormElement) {
            formElement = event.target;
        } else if (event.target instanceof Element) {
            // If the event target is not the form, look for the closest form
            formElement = event.target.closest('form');
        }
        
        if (!formElement) {
            console.error('Could not find form element');
            getMainPage().errorToastElement?.showToastAndThrow('An unexpected error occurred. Please reach out to help@proxi.co');
            return;
        }
        
        const formData = new FormData(formElement);
        const data = {
            organizationType: formData.get('organizationType'),
            organizationName: formData.get('organizationName'),
            city: formData.get('city'),
            website: formData.get('website'),
            contactName: formData.get('contactName'),
            contactEmail: formData.get('contactEmail'),
            description: formData.get('description'),
            submissionDate: new Date().toISOString()
        };

        logWithGoogleTag('city-radius-challenge-page', GoogleTagEvent.MAIN_STREET_REQUEST_SUBMITTED, {
            email: this.userEmail
        });

        try {
            // For now, we'll just log the data - later this could be sent to a backend
            const lead_info: LeadSubmission = {
                email: data.contactEmail as string,
                name: data.contactName as string,
                message: data.description as string,
                source: 'Main Street Mania Feature Request',
                additional_data: {
                    organizationType: data.organizationType as string,
                    organizationName: data.organizationName as string,
                    city: data.city as string,
                    website: data.website as string,
                    submissionDate: data.submissionDate
                }
            };
            await submitLead('mania', lead_info);
            
            // Show success message and close form
            getMainPage().errorToastElement.showToast('Thank you for your submission! We\'ll be in touch soon about featuring your community.');
            this.closeIntakeForm();
        } catch (error) {
            console.error('Error submitting form:', error);
            getMainPage().errorToastElement.showToast('There was an error submitting your request. Please try again.');
        }
    }

    @query('#guessInput')
    private readonly guessInputElement!: ProxiInputPlace;

    @query('#emailInput')
    private readonly emailInputElement!: TextField;

    @query('#otpInput')
    private readonly otpInputElement!: TextField;

    @state()
    private showOtpInput: boolean = false;

    @state()
    private pendingEmail: string = '';

    async firstUpdated() {
        this.setupOpenGraphTags();
        await this.loadGame();
        logWithGoogleTag('city-radius-challenge-page', GoogleTagEvent.MAIN_STREET_MAIN_PAGE_VISIT, {
        email: this.userEmail
        });

        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private setupOpenGraphTags() {
        // Set the OpenGraph data dynamically.
        try {
            setMetaTag('meta[property="og:title"]', 'content', `Main Street Mania - Guess the Daily Town!`);
            setMetaTag('meta[property="og:image"]', 'content', `images/mania.png`);
            setMetaTag('meta[property="og:description"]', 'content', `Join Main Street Mania and test your knowledge of towns around the U.S,! Guess today's featured Main Street location and see how close you can get`);
            if (detectMobileViewport()) {
                setManifest(`Main Street Mania`,
                    window.location.href,
                    `MS Mania`,
                    `images/mania.png`,
                    `images/mania.png`,
                    '#ffffff',
                    `purple`
                );
                setMetaTag('meta[name="apple-mobile-web-app-capable"]', 'content', 'yes');
                setMetaTag('meta[name="apple-mobile-web-app-status-bar-style"]', 'content', 'black');
                setMetaTag('meta[name="apple-mobile-web-app-title"]', 'content', `Main Street Mania`);
                setMetaTag('meta[name="apple-touch-icon"]', 'content', `images/mania.png`);
                setMetaTag('meta[name="viewport"]', 'content', 'initial-scale=1.0, viewport-fit=cover');
            };
        } catch (e) {
            console.error('Error setting all OpenGraph data', e);
        }
    }

    private async loadGame() {

        // Load daily city
        await this.loadDailyCity();
        this.loadLocalPlayerData();

        this.checkLoginStatus();

        // Load local player data and set welcome message
        this.setWelcomeMessage();

        // Comment out login stuff for now

        // Note: Don't load game state here - wait for map to be ready
        // The onMapReady() handler will load the game state when the map is actually ready
    }

    private loadLocalPlayerData() {
        const savedData = localStorage.getItem('mainStreetMania_playerData');
        if (savedData) {
            try {
                this.playerData = JSON.parse(savedData);
            } catch (error) {
                console.error('Error loading player data:', error);
                // Keep default values if parsing fails
            }
        }
    }

    private saveLocalPlayerData() {
        localStorage.setItem('mainStreetMania_playerData', JSON.stringify(this.playerData));
    }

    private async saveAccountPlayerData() {
        if (this.isLoggedIn && isDefined(accountInfo.account)) {

            const cityProgress: CityChallengeStats = {
                total_games_played: this.playerData.totalGamesPlayed,
                total_wins: this.playerData.totalWins,
                current_streak: this.playerData.currentStreak,
                longest_streak: this.playerData.longestStreak,
                last_played_date: this.playerData.lastPlayedDate,
                daily_scores: this.playerData.dailyScores
            };
            const gameProgress: GameProgress = {
                city_challenge_stats: cityProgress
            };
            await updateAccountGameProgress(accountInfo.account.email, gameProgress);
        }
    }

    private setWelcomeMessage() {
        const today = new Date().toISOString().split('T')[0];
        const hasPlayedToday = this.playerData.dailyScores[today] !== undefined;
        
        if (this.playerData.totalGamesPlayed === 0) {
            this.welcomeMessage = 'Try to guess today\'s Main Street Mania city! 🌍';
        } else if (hasPlayedToday) {
            const todayScore = this.playerData.dailyScores[today];
            this.welcomeMessage = `Welcome back! You ${todayScore.won ? 'won' : 'completed'} today's challenge with ${todayScore.guesses} guesses.`;
            this.showMainStreetInfo = true;
        } else {
            const ordinalNumber = this.getOrdinalNumber(this.playerData.totalGamesPlayed + 1);
            this.welcomeMessage = `Welcome back! Start your ${ordinalNumber} Main Street Mania 🗺️`;
        }
    }

    private getOrdinalNumber(num: number): string {
        const suffix = ['th', 'st', 'nd', 'rd'];
        const remainder = num % 100;
        return `${num}${suffix[(remainder - 20) % 10] || suffix[remainder] || suffix[0]}`;
    }

    private async loadDailyCity() {
        try {
            // Try to get the current mania location from the API
            const currentLocation = await getCurrentManiaLocation('city-radius-challenge-page');
            
            if (currentLocation) {
                // Use the location from the database
                this.gameState = {
                    ...this.gameState,
                    targetCity: currentLocation.cityState,
                    targetLat: currentLocation.lat,
                    targetLng: currentLocation.lng,
                    mainStreetName: currentLocation.markerTitle,
                    mainStreetWebsite: currentLocation.website ?? undefined,
                    mainStreetTitle: currentLocation.title
                };

                return;
            }
            
            // If no location is set for today, fall back to hardcoded communities
            // Hardcoded sample of communities for fallback
            const communities: Community[] = [
                { title: 'ATHENS MAIN STREET', cityState: 'Athens, AL', website: 'https://www.athensmainstreet.org/', tag: 'Accredited', lat: 34.8037321, lng: -86.9723068, markerTitle: 'Athens Main Street' },
                { title: 'DOWNTOWN FLORENCE ALLIANCE', cityState: 'Florence, AL', website: 'https://www.downtownflorencealliance.com/', tag: 'Accredited', lat: 34.8014776, lng: -87.6787271, markerTitle: 'Downtown Florence Alliance' },
                { title: 'BENICIA MAIN STREET', cityState: 'Benicia, CA', website: 'https://www.beniciamainstreet.org/', tag: 'Accredited', lat: 38.0445753, lng: -122.1619592, markerTitle: 'Benicia Main Street' },
                { title: 'EUREKA MAIN STREET', cityState: 'Eureka, CA', website: 'https://www.eurekamainstreet.org/', tag: 'Accredited', lat: 40.804906, lng: -124.166859, markerTitle: 'Eureka Main Street' },
                { title: 'MAIN STREET HANFORD', cityState: 'Hanford, CA', website: 'https://www.mainstreethanford.com/', tag: 'Accredited', lat: 36.3278248, lng: -119.648594, markerTitle: 'Main Street Hanford' },
                { title: 'CITY OF MONTROSE', cityState: 'Montrose, CO', website: 'https://www.cityofmontrose.org/669/Development-and-Revitalization-Team-DART', tag: 'Accredited', lat: 38.4782007, lng: -107.8766293, markerTitle: 'City of Montrose' },
                { title: 'MAIN STREET STEAMBOAT SPRINGS', cityState: 'Steamboat Springs, CO', website: 'https://www.mainstreetsteamboat.com/', tag: 'Accredited', lat: 40.49, lng: -106.83, markerTitle: 'Main Street Steamboat Springs' },
                { title: 'GEORGETOWN MAIN STREET', cityState: 'Washington, DC', website: 'https://www.georgetownmainstreet.com/', tag: 'Accredited', lat: 38.903495, lng: -77.06522, markerTitle: 'Georgetown Main Street' },
                { title: 'MAIN STREET FORT PIERCE, INC.', cityState: 'Fort Pierce, FL', website: 'https://www.mainstreetfortpierce.org/', tag: 'Accredited', lat: 27.4514092, lng: -80.3253417, markerTitle: 'Main Street Fort Pierce, Inc.' },
                { title: 'BRUNSWICK MAIN STREET', cityState: 'Brunswick, GA', website: 'https://www.discoverbrunswick.com/', tag: 'Accredited', lat: 31.15, lng: -81.49, markerTitle: 'Brunswick Main Street' },
                { title: 'MAIN STREET BATESVILLE', cityState: 'Batesville, AR', website: 'https://www.mainstreetbatesville.org/', tag: 'Accredited', lat: 35.77144, lng: -91.64969, markerTitle: 'Main Street Batesville' },
                { title: 'DOWNTOWN LITTLE ROCK PARTNERSHIP', cityState: 'Little Rock, AR', website: 'https://downtownlr.com/', tag: 'Accredited', lat: 34.743593, lng: -92.2710486, markerTitle: 'Downtown Little Rock Partnership' }
            ];

            // Pick a random city from the communities
            const randomIndex = Math.floor(Math.random() * communities.length);
            const selectedCommunity = communities[randomIndex];
            
            // Extract city name from cityState (e.g., "Athens, AL" -> "Athens")
            const cityName = selectedCommunity.cityState;

            this.gameState = {
                ...this.gameState,
                targetCity: cityName,
                targetLat: selectedCommunity.lat,
                targetLng: selectedCommunity.lng,
                mainStreetName: selectedCommunity.markerTitle,
                mainStreetWebsite: selectedCommunity.website ?? undefined,
                mainStreetTitle: selectedCommunity.title
            };
        } catch (error) {
            console.error('Error loading daily city:', error);
            // Fallback to a default city
            this.gameState = {
                ...this.gameState,
                targetCity: 'New York',
                targetLat: 40.7128,
                targetLng: -74.0060
            };
        }
    }

    private checkLoginStatus() {
        const savedEmail = localStorage.getItem('cityChallenge_email');
        if (savedEmail !== null) {
            this.isLoggedIn = true;
            this.userEmail = savedEmail;
            void this.loadUserStats();
        }
    }

    private loadGameState() {
        const today = new Date().toISOString().split('T')[0];
        let gameStateLoaded = false;

        if (this.isLoggedIn) {
            const todayStats = accountInfo.account?.game_progress?.city_challenge_stats?.daily_scores[today] ?? null;
            
            // Check if we have valid today stats from the account
            if (todayStats && accountInfo.account?.game_progress?.city_challenge_stats?.last_played_date === today) {
                // Ensure savedGuesses is always an array, regardless of what's stored
                const savedGuesses = Array.isArray(todayStats.guesses) ? todayStats.guesses : [];

                this.gameState = {
                    guesses: savedGuesses,
                    isWon: todayStats.won ?? false,
                    isGameOver: true, // Always mark as game over when loading from account (they already played)
                    targetCity: this.gameState.targetCity,
                    targetLat: this.gameState.targetLat,
                    targetLng: this.gameState.targetLng,
                    mainStreetName: this.gameState.mainStreetName,
                    mainStreetWebsite: this.gameState.mainStreetWebsite,
                    mainStreetTitle: this.gameState.mainStreetTitle
                };
                gameStateLoaded = true;

                // Always show main street info when loading completed game from account
                this.showMainStreetInfo = true;
                
                // Don't show reveal button since they already completed the game
                this.showRevealButton = false;

                // add correct place to map
                if (this.gameState.isGameOver && this.gameState.guesses.length > 0) {
                    this.addMarkerToMap(this.gameState.targetLat, this.gameState.targetLng, 15);
                }
            }
        }

        // If we don't have account stats for today, try to load from local storage
        if (!gameStateLoaded) {
            const savedState = localStorage.getItem(`mainStreetMania_game_${today}`);
            if (savedState !== null) {
                try {
                    const state = JSON.parse(savedState);
                    this.gameState = { ...this.gameState, ...state };
                } catch (error) {
                    console.error('Error loading game state:', error);
                }
            }
        }

        // Restore markers and circles on map after a brief delay to ensure map is ready
        setTimeout(() => {
            this.gameState.guesses.forEach(guess => {
                this.addMarkerToMap(guess.lat, guess.lng, guess.distance);
            });
        }, 500);
    }

    private saveGameState() {
        const today = new Date().toISOString().split('T')[0];
        const stateToSave = {
            guesses: this.gameState.guesses,
            isWon: this.gameState.isWon,
            isGameOver: this.gameState.isGameOver
        };
        localStorage.setItem(`mainStreetMania_game_${today}`, JSON.stringify(stateToSave));
    }

    private async handleGuess(value: Location) {
        try {

            // Calculate distance
            const distance = this.calculateDistance(
                value.latLng.lat,
                value.latLng.lng,
                this.gameState.targetLat,
                this.gameState.targetLng
            );

            // Calculate direction
            const direction = this.calculateDirection(
                value.latLng.lat,
                value.latLng.lng,
                this.gameState.targetLat,
                this.gameState.targetLng
            );

            // Add guess
            const guess: Guess = {
                location: value.search,
                distance: Math.round(distance),
                direction,
                lat: value.latLng.lat,
                lng: value.latLng.lng
            };

            this.gameState = {
                ...this.gameState,
                guesses: [...this.gameState.guesses, guess]
            };

            // Add marker to map
            this.addMarkerToMap(value.latLng.lat, value.latLng.lng, distance);

            // Check win condition
            if (distance < 15) {
                this.handleWin();
            } else if (this.gameState.guesses.length >= 6) {
                this.handleGameOver();
            }

            // Save state
            this.saveGameState();

            // Clear input
            this.guessInputElement.resetSearch();
            this.guessInputElement.resetValue();
        } catch (error) {
            console.error('Error processing guess:', error);
            alert('Error processing your guess. Please try again.');
        }
    }

    private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
        const R = 3959; // Earth's radius in miles
        const dLat = this.toRad(lat2 - lat1);
        const dLng = this.toRad(lng2 - lng1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRad(lat1)) *
            Math.cos(this.toRad(lat2)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    private toRad(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    private calculateDirection(lat1: number, lng1: number, lat2: number, lng2: number): string {
        const dLng = lng2 - lng1;
        const y = Math.sin(dLng) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
        const bearing = Math.atan2(y, x);
        const degrees = (bearing * 180) / Math.PI;
        const normalized = (degrees + 360) % 360;

        if (normalized >= 337.5 || normalized < 22.5) return 'N';
        if (normalized >= 22.5 && normalized < 67.5) return 'NE';
        if (normalized >= 67.5 && normalized < 112.5) return 'E';
        if (normalized >= 112.5 && normalized < 157.5) return 'SE';
        if (normalized >= 157.5 && normalized < 202.5) return 'S';
        if (normalized >= 202.5 && normalized < 247.5) return 'SW';
        if (normalized >= 247.5 && normalized < 292.5) return 'W';
        return 'NW';
    }

    private async onMapReady() {
        while (this.googleMapComponent?.map?.getBounds() === undefined) {
            await delay(0);
        }
        this.mapReady = true;
        this.loadGameState();
    }

    private addMarkerToMap(lat: number, lng: number, distance: number) {
        if (!this.mapReady || this.googleMapComponent?.map === undefined) {
            console.warn('Cannot add marker: map is not ready yet');
            // Retry after a delay if map isn't ready
            setTimeout(() => {
                if (this.mapReady && this.googleMapComponent?.map !== undefined) {
                    this.addMarkerToMap(lat, lng, distance);
                }
            }, 1000);
            return;
        }

        const color = this.getColorForDistance(distance);
        const roundedDistance = this.roundedDistance(distance);

        // Create marker
        const marker = new google.maps.Marker({
            position: { lat, lng },
            map: this.googleMapComponent.map,
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: color,
                fillOpacity: 0.9,
                strokeColor: '#ffffff',
                strokeWeight: 3
            },
            title: `${Math.round(distance)} miles from target`
        });

        // round the distance up to nearest 100 miles


        // Create circle with radius representing the miss distance
        const circle = new google.maps.Circle({
            strokeColor: color,
            strokeOpacity: 0.5,
            strokeWeight: 100,
            fillColor: color,
            fillOpacity: 0.5,
            map: this.googleMapComponent.map,
            center: { lat, lng },
            radius: roundedDistance * 1609.34,
            clickable: false
        });

        // Add pulsing animation for recent guesses
        if (this.gameState.guesses.length > 0) {
            setTimeout(() => {
                circle.setOptions({
                    strokeOpacity: 0.4,
                    fillOpacity: 0.05
                });
            }, 2000);
        }

        // make circle less prominent after 5 seconds
        setTimeout(() => {
            circle.setOptions({
                strokeOpacity: 0.1,
                fillOpacity: 0.04
            });
        }, 5000);

        this.markers.push(marker);
        this.circles.push(circle);
    }

    private adjustMapBounds() {
        if (!this.mapReady || this.googleMapComponent?.map === undefined || this.markers.length === 0) return;

        // Create a new bounds object
        const bounds = new google.maps.LatLngBounds();

        // Include all markers in bounds
        this.markers.forEach(marker => {
            const markerLocation = marker.getPosition();
            if (isDefined(markerLocation)) {
                bounds.extend(markerLocation);
            }
        });

        // Include target location if game is over
        if (this.gameState.isGameOver) {
            bounds.extend({ lat: this.gameState.targetLat, lng: this.gameState.targetLng });
        }

        // Only adjust bounds if we have multiple points or game is over
        // For single points, avoid zooming in too far
        const totalPoints = this.markers.length + (this.gameState.isGameOver ? 1 : 0);
        
        if (totalPoints > 1) {
            // Fit the map to the calculated bounds with some padding
            this.googleMapComponent.map.fitBounds(bounds, { padding: 50 });
        } else if (totalPoints === 1) {
            // For single point, just center on it but don't zoom in too far
            const center = bounds.getCenter();
            this.googleMapComponent.map.setCenter(center);
            // Keep zoom level reasonable (don't zoom in past level 8)
            const currentZoom = this.googleMapComponent.map.getZoom();
            if (currentZoom > 8) {
                this.googleMapComponent.map.setZoom(8);
            }
        }
    }

    private getColorForDistance(distance: number): string {
        if (distance < 15) return '#28a745'; // Green - Success
        if (distance < 50) return '#fd7e14'; // Orange - Hot
        if (distance < 250) return '#ffc107'; // Yellow - Warm
        return '#6c757d'; // Gray - Far
    }

    private roundedDistance(distance: number): number {
        if (distance < 15) return 15;
        if (distance < 25) return Math.ceil(distance / 2) * 2;
        if (distance < 50) return Math.ceil(distance / 5) * 5;
        if (distance < 100) return Math.ceil(distance / 10) * 10;
        if (distance < 500) return Math.ceil(distance / 50) * 50;
        if (distance < 1000) return Math.ceil(distance / 100) * 100;
        return Math.ceil(distance / 500) * 500;
    }

    private getEmojiForDistance(distance: number): string {
        if (distance < 15) return '✅';
        if (distance < 50) return '🔥';
        if (distance < 250) return '🟨';
        return '❄️';
    }

    private handleWin() {
        this.gameState = {
            ...this.gameState,
            isWon: true,
            isGameOver: true
        };

        // Add the target location marker with a star icon
        this.addTargetMarkerToMap();

        // Show main street information
        this.showMainStreetInfo = true;

        // Save local player data
        this.saveGameResult(true);

        // save account player data if logged in
        if (this.isLoggedIn) {
            void this.saveAccountPlayerData();
        }

        // Show win modal
        this.showWinModal = true;
    }

    private handleGameOver() {
        this.gameState = {
            ...this.gameState,
            isGameOver: true
        };

        // Save local player data
        this.saveGameResult(false);

        // save account player data if logged in
        if (this.isLoggedIn) {
            void this.saveAccountPlayerData();
        }

        // Show the reveal button instead of immediately showing the answer
        this.showRevealButton = true;
    }

    private saveGameResult(won: boolean) {
        const today = new Date().toISOString().split('T')[0];
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayString = yesterday.toISOString().split('T')[0];

        // Check if already played today
        if (this.playerData.dailyScores[today] !== undefined) {
            return; // Already saved today's score
        }

        // Update daily score
        this.playerData.dailyScores[today] = {
            won,
            guesses: this.gameState.guesses.length,
            city: this.gameState.targetCity
        };

        // Update totals
        this.playerData.totalGamesPlayed++;
        if (won) {
            this.playerData.totalWins++;
        }

        // Update streak
        if (won) {
            if (this.playerData.lastPlayedDate === yesterdayString && this.playerData.dailyScores[yesterdayString]?.won) {
                this.playerData.currentStreak++;
            } else {
                this.playerData.currentStreak = 1;
            }
            
            if (this.playerData.currentStreak > this.playerData.longestStreak) {
                this.playerData.longestStreak = this.playerData.currentStreak;
            }
        } else {
            this.playerData.currentStreak = 0;
        }

        this.playerData.lastPlayedDate = today;
        this.saveLocalPlayerData();
        void this.saveAccountPlayerData();
    }

    private revealAnswer() {
        this.showRevealButton = false;
        
        // Add the target location marker with a star icon
        this.addTargetMarkerToMap();
        
        // Show main street information
        this.showMainStreetInfo = true;
        
        // Show the win modal for sharing
        this.showWinModal = true;
    }

    private addTargetMarkerToMap() {
        if (!this.mapReady || this.googleMapComponent?.map === undefined) {
            console.warn('Cannot add target marker: map is not ready yet');
            return;
        }

        // Create star marker for the target location
        const targetMarker = new google.maps.Marker({
            position: { lat: this.gameState.targetLat, lng: this.gameState.targetLng },
            map: this.googleMapComponent.map,
            icon: {
                path: 'M12,2L13.09,8.26L22,9L17,14L18.18,22L12,19L5.82,22L7,14L2,9L10.91,8.26L12,2Z', // Star path
                scale: 2,
                fillColor: '#FFD700', // Gold color
                fillOpacity: 1,
                strokeColor: '#FF6B00', // Orange border
                strokeWeight: 2,
                anchor: new google.maps.Point(12, 12)
            },
            title: `Target: ${this.gameState.targetCity}`,
            zIndex: 1000 // Ensure it appears above other markers
        });

        this.markers.push(targetMarker);

        // Adjust map bounds to include the target location
        this.adjustMapBounds();

        // Zoom to show target location prominently
        setTimeout(() => {
            if (this.googleMapComponent?.map !== undefined) {
                this.googleMapComponent.map.setCenter({ lat: this.gameState.targetLat, lng: this.gameState.targetLng });
                this.googleMapComponent.map.setZoom(10);
            }
        }, 1000);
    }

    private async handleLogin() {
        const email = this.emailInputElement.value.trim().toLowerCase();
        if (!email) return;

        try {
            await login('Game', email, LoginOrigin.Game);
            this.pendingEmail = email;
            this.showOtpInput = true;
            getMainPage().errorToastElement.showToast('Check your email for the verification code!');
        } catch (error) {
            console.error('Error sending OTP:', error);
            getMainPage().errorToastElement.showToastAndThrow('Error sending verification code. Please try again.');
        }
    }

    async login(email: string) {
        const validEmail = validateEmail(email);
        if (!validEmail) {
            getMainPage().errorToastElement.showToastAndThrow('Please enter a valid email address.');
            return;
        }

        await login('Game', email, LoginOrigin.Game);
    }

    private async handleVerifyOtp() {
        const otp = this.otpInputElement.value.trim();
        if (!otp) return;

        try {
            await this.confirmEmail(this.pendingEmail, otp);
            this.isLoggedIn = true;
            await accountInfo.instantiateAccountData(false);
            this.userEmail = accountInfo.account?.email ?? '';
            localStorage.setItem('cityChallenge_email', this.userEmail);
            
            this.showOtpInput = false;
            this.pendingEmail = '';

            // if user had already played today, save their stats to their account
            const today = new Date().toISOString().split('T')[0];
            if (this.playerData.dailyScores[today] !== undefined) {
                await this.saveAccountPlayerData();
                await accountInfo.instantiateAccountData(false);
            }
            
            await this.loadUserStats();
        } catch (error) {
            console.error('Error verifying OTP:', error);
            alert('Invalid verification code. Please try again.');
        }
    }

    private async confirmEmail(email: string, code: string, bookmark?: boolean) {
        try {
            await upgradeToken('Game', email, code, undefined, LoginOrigin.Game);
            await accountInfo.instantiateAccountData(false);
        } catch (e) {
            getMainPage().errorToastElement.showToastAndThrow('There was an error verifying your email. Please try again.');
        }
    }

    private async loadUserStats() {
        try {
            const game_progress = accountInfo.account?.game_progress;
            if (isDefined(game_progress)) {
                this.playerData.totalGamesPlayed = game_progress.city_challenge_stats?.total_games_played ?? 0;
                this.playerData.totalWins = game_progress.city_challenge_stats?.total_wins ?? 0;
                this.playerData.currentStreak = game_progress.city_challenge_stats?.current_streak ?? 0;
                this.playerData.longestStreak = game_progress.city_challenge_stats?.longest_streak ?? 0;
                this.playerData.lastPlayedDate = game_progress.city_challenge_stats?.last_played_date ?? '';
                this.playerData.dailyScores = game_progress.city_challenge_stats?.daily_scores ?? {};
                this.saveLocalPlayerData();
                this.setWelcomeMessage();
            } else {
                // get from local storage if not in account info
                this.loadLocalPlayerData();
                this.setWelcomeMessage();
            }
        } catch (error) {
            console.error('Error loading user stats:', error);
        }
    }

    private resetGame() {
        // Clear game state
        this.gameState = {
            guesses: [],
            isWon: false,
            isGameOver: false,
            targetCity: this.gameState.targetCity,
            targetLat: this.gameState.targetLat,
            targetLng: this.gameState.targetLng
        };

        // Clear all markers and circles from map
        this.markers.forEach(marker => {
            if (marker.getMap()) {
                marker.setMap(null);
            }
        });
        this.markers.length = 0;
        
        this.circles.forEach(circle => {
            if (circle.getMap()) {
                circle.setMap(null);
            }
        });
        this.circles.length = 0;

        // Clear saved game state from localStorage
        const today = new Date().toISOString().split('T')[0];
        localStorage.removeItem(`mainStreetMania_game_${today}`);

        // clear saved player data
        localStorage.removeItem('mainStreetMania_playerData');
        this.playerData = {
            totalGamesPlayed: 0,
            totalWins: 0,
            currentStreak: 0,
            longestStreak: 0,
            lastPlayedDate: '',
            dailyScores: {}
        };
        this.setWelcomeMessage();

        // Reset modal states
        this.showWinModal = false;
        this.showLeaderboard = false;
        this.showRevealButton = false;
        this.showMainStreetInfo = false;

        // Clear input field
        if (isDefined(this.guessInputElement)) {
            this.guessInputElement.resetSearch();
            this.guessInputElement.resetValue();
        }

        // Reset map view to world view
        if (this.mapReady && isDefined(this.googleMapComponent?.map)) {
            this.googleMapComponent.map.setCenter({ lat: 20, lng: 0 });
            this.googleMapComponent.map.setZoom(2);
        }
    }

    private async loadLeaderboard() {
        try {
            const response = await fetch('/api/city-challenge/leaderboard');
            if (!response.ok) {
                throw new Error('Failed to fetch leaderboard');
            }
            
            this.leaderboardData = await response.json();
            this.showLeaderboard = true;
        } catch (error) {
            console.error('Error loading leaderboard:', error);
            alert('Failed to load leaderboard. Please try again later.');
        }
    }

    private generateShareText(): string {
        let text = `🌍 I ${this.gameState.isWon ? 'won' : 'played'} Main Street Mania today!\n\n`;
        this.gameState.guesses.forEach((guess, index) => {
            const emoji = this.getEmojiForDistance(guess.distance);
            text += `${emoji}📍 ${guess.distance} mi\n`;
        });
        
        // Add streak information
        if (this.playerData.currentStreak > 0) {
            text += `\n🔥 Current streak: ${this.playerData.currentStreak} day${this.playerData.currentStreak > 1 ? 's' : ''}`;
        }
        
        if (this.playerData.longestStreak > 0 && this.playerData.longestStreak !== this.playerData.currentStreak) {
            text += `\n🏆 Best streak: ${this.playerData.longestStreak} day${this.playerData.longestStreak > 1 ? 's' : ''}`;
        }
        
        text += '\nCan you beat my score? #MainStreetMania #ProxiMaps';
        text += '\nhttps://mania.proxi.co';
        return text;
    }

    copyShareTextToClipboard() {
        const shareText = this.generateShareText();
        navigator.clipboard.writeText(shareText).then(() => {
            alert('Share text copied to clipboard!');
        }).catch(err => {
            console.error('Could not copy text: ', err);
            alert('Failed to copy share text. Please try manually.');
        });

        logWithGoogleTag('city-radius-challenge-page', GoogleTagEvent.MAIN_STREET_SHARE_CLICKED, {
            email: this.userEmail
        });
    }

    render() {
        return html`
            <div class="container">
                <div class="header">
                    <h1>🌍 Main Street Mania</h1>
                    <p>Can you find today's Main Street Area?</p>
                    <br>
                    <div class="how-to-play" style="font-size: 1.1em; padding: 5px;">
                        <strong>How to Play:</strong>
                        <ul style="margin: 5px 0 0 10px; padding: 0;list-style-type: none;">
                            <li>Enter any location in the "guess" box below and submit your first guess.</li>
                            <li>Your guess will be marked on the map with a colored circle indicating roughly how far you are from the target.</li>
                            <li>You have up to 6 guesses to find the target city/town.</li>
                            <li><b>Get within 15 miles to win!</b></li>
                        </ul>
                    </div>
                    ${this.isLoggedIn ? html`
                        Playing as: <strong>${this.userEmail}</strong>
                    ` : ''}
                </div>

                ${
                !this.isLoggedIn ? html`
                    <div class="login-section">
                        ${!this.showOtpInput ? html`
                            <div class="login-form">
                                <span style="margin-right: 10px;">Optional: Login to save your progress</span>
                                <proxi-textfield
                                    id="emailInput"
                                    label="Email"
                                    type="email"
                                    style="max-width: 300px;"
                                ></proxi-textfield>
                                <button class="btn btn-primary" @click=${this.handleLogin}>
                                    Send Code
                                </button>
                            </div>
                        ` : html`
                            <div class="login-form">
                                <span style="margin-right: 10px;">Enter verification code from email:</span>
                                <proxi-textfield
                                    id="otpInput"
                                    label="Verification Code"
                                    style="max-width: 200px;"
                                ></proxi-textfield>
                                <button class="btn btn-primary" @click=${this.handleVerifyOtp}>
                                    Verify
                                </button>
                            </div>
                        `}
                    </div>
                ` : html`
                    <div class="welcome-section">
                        <div style="text-align: center; padding: 20px; background: #f8f9fa; border-bottom: 1px solid #dee2e6;">
                            <p style="margin: 0; font-size: 1.1em; font-weight: 600; color: #667eea;">
                                ${this.welcomeMessage}
                            </p>
                        </div>
                        <div class="stats">
                            <div class="stat-item">
                                <div class="stat-value">${this.playerData.currentStreak}</div>
                                <div class="stat-label">Current Streak</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-value">${this.playerData.totalWins}/${this.playerData.totalGamesPlayed}</div>
                                <div class="stat-label">Games Won</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-value">${this.playerData.longestStreak}</div>
                                <div class="stat-label">Best Streak</div>
                            </div>
                        </div>
                    </div>
                `}
               <div class="game-section">
                    ${this.gameState.isGameOver && !this.showRevealButton ? html`
                        <div class="final-reveal" style="margin-top: 20px; text-align: center;">
                            <h2>Today's target city is: ${this.gameState.targetCity}</h2>
                            ${this.showMainStreetInfo && this.gameState.mainStreetName ? html`
                                <div class="main-street-info" style="margin-top: 20px; padding: 20px; background: #f8f9fa; border-radius: 12px;">
                                    <h3 style="margin: 0 0 10px 0; color: #667eea;">Featured Main Street Organization</h3>
                                    <p style="margin: 5px 0; font-weight: 600;">${this.gameState.mainStreetTitle}</p>
                                    <p style="margin: 5px 0; color: #6c757d;">${this.gameState.mainStreetName}</p>
                                    ${this.gameState.mainStreetWebsite ? html`
                                        <div style="margin-top: 15px;">
                                            <a
                                                href="${this.gameState.mainStreetWebsite}"
                                                target="_blank"
                                                style="display: inline-block; padding: 10px 20px; background: #667eea; color: white; text-decoration: none; border-radius: 8px; font-weight: 600;"
                                            >
                                                Visit ${this.gameState.mainStreetName} Website
                                            </a>
                                        </div>
                                    ` : ''}
                                </div>
                            ` : ''}
                        </div>
                        <div class="share-section" style="text-align: center; margin-top: 10px;margin-bottom: 20px;">
                            <button class="btn btn-primary" @click=${() => { this.showWinModal = true; }}>
                                Share Your Score
                            </button>
                        </div>
                    ` : ''}
                    <div class="inner-section" style="display: flex; ${detectMobileViewport() ? 'flex-direction: column; align-items: center;gap: 5px;' : 'flex-direction: row; align-items: flex-start;gap: 20px;'}">
                        <div class="map-container">
                            <google-map
                                id="map"
                                api-key="${GOOGLE_KEY}"
                                min-zoom="1"
                                latitude=40
                                longitude=-98
                                zoom="3"
                                click-events
                                @google-map-ready="${async () => await this.onMapReady()}"
                                drag-events
                                disable-street-view-control
                                disable-map-type-control
                                disable-zoom-control
                                disable-default-ui
                                single-info-window
                                .additionalMapOptions=${{
                                    clickableIcons: false,
                                    keyboardShortcuts: false,
                                    mapId: 'f71d5ee61f112aa4'
                                }}
                            >
                            </google-map>
                        </div>

                        <div class="guesses-list ${this.gameState.guesses.length === 0 ? 'no-guess-yet' : ''}  ${this.gameState.isGameOver && this.gameState.guesses.length === 0 ? 'hidden' : '' }">
                            <h3 style="margin-top: 0;">Your Guesses</h3>
                            ${this.gameState.guesses.length === 0 ? html`
                                <p style="font-size: 0.9em; color: #6c757d; margin-top: 0; margin-bottom: 10px;">
                                    Make your first guess to get started!
                                </p>
                            ` : this.gameState.isWon ? '' : this.gameState.guesses.length < 6 ? html`
                                <p style="font-size: 0.9em; color: #6c757d; margin-top: 0; margin-bottom: 10px;">
                                    You have ${6 - this.gameState.guesses.length} guess${(6 - this.gameState.guesses.length) === 1 ? '' : 'es'} left.
                                </p>
                            ` : ''}
                            ${this.gameState.guesses.length > 0 ? html`
                                <p style="font-size: 0.9em; color: #6c757d; margin-top: 0; margin-bottom: 10px;">
                                    ${this.gameState.isWon ? 'You found the target city! 🎉' : 'See how close you were with each guess:'}
                                </p>
                            ` : ''}
                            ${this.gameState.guesses.map((guess, index) => html`
                                <div class="guess-item">
                                    <span class="guess-emoji">${this.getEmojiForDistance(guess.distance)}</span>
                                    <span class="guess-location">${guess.location}</span>
                                    <span class="guess-distance">
                                        ${guess.distance} mi
                                    </span>
                                </div>
                            `)}
                            ${!this.gameState.isGameOver ? html`
                                <div class="guess-item" style="margin-top: 15px;">
                                    <proxi-input-place
                                        id="guessInput"
                                        class="input"
                                        .searchTypes=${['locality', 'administrative_area_level_2', 'administrative_area_level_1', 'sublocality', 'postal_code']}
                                        .customMap=${this.googleMapComponent?.map}
                                        placeholder="Enter your ${this.gameState.guesses.length === 0 ? 'first' : 'next'} guess..."
                                        @value-changed="${async (e: CustomEvent) => {
                                            if (!isDefined(e.detail.value)) return;
                                            await this.handleGuess(e.detail.value);
                                        }}"
                                        required>
                                    </proxi-input-place>
                                </div>
                            ` : ''}
                            ${this.showRevealButton ? html`
                                <div class="reveal-section" style="margin-top: 20px; text-align: center;">
                                    <h3>Game Over!</h3>
                                    <p>You've used all your guesses.</p>
                                    <button class="btn btn-primary" @click=${this.revealAnswer}>
                                        Reveal Answer
                                    </button>
                                </div>
                            ` : ''}
                        </div>
                    </div>
                    <div style="text-align: center; margin-top: 30px;">
                         ${this.showIntakeForm ? html`
                            <form-modal
                                title="Request to be Featured"
                                @close="${this.closeIntakeForm}"
                            >
                                <form @submit="${this.submitIntakeForm}" class="intake-form-content">
                                    <div class="form-group">
                                        <label for="organizationType">Organization Type</label>
                                        <select name="organizationType" id="organizationType" required>
                                            <option value="">Select type...</option>
                                            <option value="main-street">Main Street America Organization</option>
                                            <option value="downtown-association">Downtown Association</option>
                                            <option value="city-government">City Government</option>
                                            <option value="chamber-of-commerce">Chamber of Commerce</option>
                                            <option value="business-improvement-district">Business Improvement District</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div class="form-group">
                                        <label for="organizationName">Organization Name</label>
                                        <input type="text" name="organizationName" id="organizationName" required>
                                    </div>

                                    <div class="form-group">
                                        <label for="city">City or Town</label>
                                        <input type="text" name="city" id="city" required>
                                    </div>

                                    <div class="form-group">
                                        <label for="contactName">Contact Name</label>
                                        <input type="text" name="contactName" id="contactName" required>
                                    </div>
                                    <div class="form-group">
                                        <label for="website">Website</label>
                                        <input type="url" name="website" id="website">
                                    </div>
                                    <div class="form-group">
                                        <label for="contactEmail">Contact Email</label>
                                        <input type="email" name="contactEmail" id="contactEmail" required>
                                    </div>

                                    <div class="form-group">
                                        <label for="description">Tell us about your community and why you'd like to be featured</label>
                                        <textarea name="description" id="description" placeholder="Describe your downtown area, main street, special attractions, events, or what makes your community unique..." required></textarea>
                                    </div>

                                    <div class="form-actions">
                                        <button type="button" class="form-button secondary" @click="${this.closeIntakeForm}">
                                            Cancel
                                        </button>
                                        <button type="submit" class="form-button primary" @click="${this.submitIntakeForm}">
                                            Submit Request
                                        </button>
                                    </div>
                                </form>
                            </form-modal>
                        ` : html`
                            <button
                                class="feature-request-button"
                                @click="${this.showFeatureForm}"
                            >
                                Want to have your city, main street, or downtown association featured soon? Let us know!
                            </button>
                        `}
                    </div>
                    ${/* Commented out leaderboard for now since it requires login
                    this.isLoggedIn ? html`
                        <button 
                            class="btn btn-primary" 
                            @click=${this.loadLeaderboard}
                            style="margin-top: 20px;"
                        >
                            View Leaderboard
                        </button>
                    ` : */ ''}
                </div>
            </div>
        </div>
        <div class="spacer"></div>
        <div class="container" style="margin-top: 40px; margin-bottom: 40px;">
            <!-- Sponsor and Blog Section -->
            <div class="sponsor-section">
                <div class="sponsor-content">
                    <div class="sponsor-title">
                        🗺️ Main Street Mania is proudly built by Proxi
                    </div>
                    <div class="sponsor-description">
                        Proxi empowers Main Street organizations with interactive maps, digital guides, community passports, and revenue-generating tools that strengthen local economies and enhance visitor experiences.
                    </div>
                    <div @click=${() => {
                        logWithGoogleTag('city-radius-challenge-page', GoogleTagEvent.MAIN_STREET_PROXI_CLICKED, {
                            email: this.userEmail
                        });
                        window.open('https://www.proxi.co/maps-for/main-streets', '_blank');
                    }}
                       class="sponsor-cta">
                        Learn How Proxi Serves Main Streets →
                    </div>

                    <div class="blog-section">
                        <div class="blog-title">Latest from the Proxi Blog</div>
                        <div class="blog-grid">
                            ${this.blogLinks.map(blog => html`
                                <div class="blog-item">
                                    <div @click=${() => {
                                        logWithGoogleTag('city-radius-challenge-page', GoogleTagEvent.MAIN_STREET_PROXI_CLICKED, {
                                            email: this.userEmail,
                                            blogTitle: blog.title
                                        });
                                        window.open(blog.url, '_blank');
                                    }} class="blog-item-link">
                                        <div class="blog-item-title">${blog.title}</div>
                                        <div class="blog-item-description">${blog.description}</div>
                                    </div>
                                </div>
                            `)}
                        </div>
                    </div>
                </div>
            </div>

            ${this.showWinModal ? html`
                <div class="modal" @click=${(e: MouseEvent) => {
                    if (e.target === e.currentTarget) {
                        this.showWinModal = false;
                    }
                }}>
                    <div class="modal-content">
                        <h2>${this.gameState.isWon ? '🎉 You Win!' : '🏁 Game Over'}</h2>
                        ${this.gameState.isWon ? html`
                            <p style="font-size: 1.2em;">
                                You found ${this.gameState.targetCity} in ${this.gameState.guesses.length} guesses!
                            </p>
                        ` : html`
                            <p style="font-size: 1.2em;">
                                Better luck next time! The city was <br>
                                <span style="font-weight: 600; font-size: 1.2em;">${this.gameState.targetCity}</span>.
                            </p>
                        `}

                        ${this.gameState.mainStreetName && this.gameState.mainStreetTitle ? html`
                            <div class="modal-main-street-info" style="margin: 20px 0; padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #667eea;">
                                <h4 style="margin: 0 0 8px 0; color: #667eea; font-size: 1em;">🏛️ Featured Main Street Organization</h4>
                                <p style="margin: 4px 0; font-weight: 600; font-size: 0.95em;">${this.gameState.mainStreetTitle}</p>
                                <p style="margin: 4px 0; color: #6c757d; font-size: 0.9em;">${this.gameState.mainStreetName}</p>
                                ${this.gameState.mainStreetWebsite ? html`
                                    <p style="margin: 8px 0 0 0;">
                                        <a 
                                            href="${this.gameState.mainStreetWebsite}"
                                            target="_blank"
                                            style="color: #667eea; text-decoration: none; font-weight: 500; font-size: 0.9em;"
                                        >
                                            Visit Website →
                                        </a>
                                    </p>
                                ` : ''}
                            </div>
                        ` : ''}

                        <div class="scorecard">
                            <h3 style="margin-top: 0;">Your Score</h3>
                            ${this.gameState.guesses.map(guess => html`
                                <div class="scorecard-row">
                                     ${this.getEmojiForDistance(guess.distance)} 📍${guess.distance} mi
                                </div>
                            `)}
                        </div>

                        <div class="share-buttons">
                            <button class="btn btn-primary" @click=${this.copyShareTextToClipboard}>
                                Share Your Score
                            </button>
                            <button class="btn" style="background: #6c757d; color: white;" 
                                @click=${() => {
                                    this.showWinModal = false;
                                }}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ` : ''}

            ${this.showLeaderboard ? html`
                <div class="modal" @click=${(e: MouseEvent) => {
                    if (e.target === e.currentTarget) {
                        this.showLeaderboard = false;
                    }
                }}>
                    <div class="modal-content">
                        <h2>🏆 Leaderboard</h2>
                        <div class="leaderboard">
                            ${this.leaderboardData.map((entry, index) => html`
                                <div class="leaderboard-item">
                                    <span>${index + 1}. ${entry.email}</span>
                                    <span>${entry.score} guesses | ${entry.streak} day streak</span>
                                </div>
                            `)}
                        </div>
                        <button class="btn btn-primary" style="margin-top: 20px;" 
                            @click=${() => {
                                this.showLeaderboard = false;
                            }}>
                            Close
                        </button>
                    </div>
                </div>
            ` : ''}
        `;
    }

    static styles = css`
        :host {
            display: block;
            font-family: 'Montserrat', sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            padding: 20px;
            overflow-x: hidden;
            overflow-y: auto;
        }

        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            overflow: hidden;
        }

        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 40px;
            text-align: center;
        }

        .header h1 {
            margin: 0;
            font-size: 2.5em;
            font-weight: 700;
        }

        .header p {
            margin: 10px 0 0 0;
            font-size: 1.2em;
            opacity: 0.9;
        }

        .login-section {
            padding: 20px 40px;
            background: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
        }

        .login-form {
            display: flex;
            gap: 10px;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
        }

        .game-section {
            padding: 20px;
        }

        .input-group {
            display: flex;
            gap: 10px;
            margin-bottom: 20px;
        }

        .input-group mwc-textfield {
            flex: 1;
        }

        .btn {
            padding: 12px 24px;
            border: none;
            border-radius: 8px;
            font-size: 1em;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s;
        }

        .btn-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
        }

        .btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .btn-primary:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none;
        }

        .map-container {
            width: 100%;
            height: 500px;
            border-radius: 12px;
            overflow: hidden;
            margin-bottom: 20px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .feature-request-button {
            display: block;
            margin: 15px auto;
            padding: 12px 24px;
            background: linear-gradient(135deg, #667eea, #764ba2);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            text-align: center;
            transition: all 0.3s ease;
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .feature-request-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .intake-form-content {
            padding: 20px;
        }

        .form-group {
            margin-bottom: 20px;
        }

        .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 600;
            color: #333;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
            width: 100%;
            padding: 12px;
            border: 1px solid #ddd;
            border-radius: 6px;
            font-size: 14px;
            box-sizing: border-box;
        }

        .form-group textarea {
            height: 100px;
            resize: vertical;
        }

        .form-actions {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
            margin-top: 20px;
        }

        .form-button {
            padding: 10px 20px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
        }

        .form-button.primary {
            background: #667eea;
            color: white;
        }

        .form-button.secondary {
            background: #e5e7eb;
            color: #374151;
        }

        .guesses-list {
            background: #f8f9fa;
            border-radius: 12px;
            padding: 20px;
            min-width: 30%;
        }

        .no-guess-yet {
            background-color: #e5e7eb;
            color: #374151;
        }

        .hidden {
            display: none;
        }

        .guess-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px;
            background: white;
            border-radius: 8px;
            margin-bottom: 10px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .guess-location {
            flex: 1;
            font-weight: 600;
        }

        .guess-distance {
            font-size: 0.9em;
            color: #6c757d;
        }

        .guess-emoji {
            font-size: 1.5em;
        }

        .modal {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        .modal-content {
            background: white;
            border-radius: 20px;
            padding: 40px;
            max-width: 600px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
            text-align: center;
        }

        .modal-content h2 {
            margin: 0 0 20px 0;
            font-size: 2em;
        }

        .modal-main-street-info {
            text-align: left;
            transition: all 0.3s ease;
        }

        .modal-main-street-info:hover {
            background: #f1f3f4 !important;
            transform: translateY(-1px);
        }

        .modal-main-street-info a:hover {
            color: #5a67d8 !important;
            text-decoration: underline !important;
        }

        .scorecard {
            background: #f8f9fa;
            border-radius: 12px;
            padding: 20px;
            margin: 20px 0;
            text-align: left;
        }

        .scorecard-row {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px;
            font-family: monospace;
            font-size: 1.1em;
        }

        .share-buttons {
            display: flex;
            gap: 10px;
            justify-content: center;
            margin-top: 20px;
        }

        .leaderboard {
            margin-top: 20px;
        }

        .leaderboard-item {
            display: flex;
            justify-content: space-between;
            padding: 12px;
            background: white;
            border-radius: 8px;
            margin-bottom: 8px;
        }

        .stats {
            display: flex;
            gap: 30px;
            justify-content: center;
            margin: 20px 0;
        }

        .stat-item {
            text-align: center;
        }

        .stat-value {
            font-size: 2em;
            font-weight: 700;
            color: #667eea;
        }

        .stat-label {
            font-size: 0.9em;
            color: #6c757d;
        }

        .main-street-info {
            border: 2px solid #667eea;
            position: relative;
        }

        .main-street-info::before {
            content: '🏛️';
            position: absolute;
            top: -10px;
            left: 20px;
            background: white;
            padding: 0 10px;
            font-size: 1.5em;
        }



        .sponsor-section {
            background: #f8f9fa;
            border-top: 1px solid #dee2e6;
            padding: 40px;
            text-align: center;
        }

        .sponsor-content {
            max-width: 800px;
            margin: 0 auto;
        }

        .sponsor-title {
            font-size: 1.3em;
            font-weight: 600;
            color: #495057;
            margin-bottom: 15px;
        }

        .sponsor-description {
            font-size: 1.1em;
            color: #6c757d;
            margin-bottom: 20px;
            line-height: 1.6;
        }

        .sponsor-cta {
            display: inline-block;
            padding: 12px 30px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-decoration: none;
            border-radius: 25px;
            font-weight: 600;
            transition: all 0.3s ease;
            margin-bottom: 40px;
        }

        .sponsor-cta:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
        }

        .blog-section {
            margin-top: 40px;
        }

        .blog-title {
            font-size: 1.5em;
            font-weight: 700;
            color: #495057;
            margin-bottom: 25px;
        }

        .blog-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            text-align: left;
        }

        .blog-item {
            background: white;
            border-radius: 8px;
            padding: 20px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;
            border-left: 4px solid #667eea;
        }

        .blog-item:hover {
            transform: translateY(-3px);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
        }

        .blog-item-title {
            font-weight: 600;
            font-size: 1em;
            color: #495057;
            margin-bottom: 8px;
            line-height: 1.4;
        }

        .blog-item-description {
            font-size: 0.9em;
            color: #6c757d;
            line-height: 1.5;
        }

        .blog-item a {
            text-decoration: none;
            color: inherit;
        }

        @media (max-width: 768px) {
            .header h1 {
                font-size: 1.8em;
            }

            .header p {
                font-size: 1em;
            }

            .game-section {
                padding: 10px;
            }

            .map-container {
                height: 300px;
            }

            .guesses-list {
                padding: 10px;
                width: 100%;
            }
        }
    `;
}
